/** Form building blocks shared by the signin flow and future account forms:
 * labeled inputs and the busy state that disables a container's controls
 * while a submit is in flight (spinner styling comes from mba.css).
 */

import { h } from "../lib/jsx";

export interface Field {
  /** The nodes to place in the form (label + input). */
  nodes: Element[];
  input: HTMLInputElement;
}

/** A labeled .otp-input. `attrs` land on the input (type, autocomplete,
 * inputmode, pattern…).
 *
 * The label is always visually hidden: every field in the finalized design is
 * placeholder-styled, so the label exists to give the input an accessible name
 * rather than to be read (codeInput does the same for its code group). It also
 * keeps `.otp-row`'s equal-width `> *` rule yielding two columns per row rather
 * than four. Both callers used to reach into `nodes[0]` and add the class
 * themselves — one decision written out twice. A field that wants a visible
 * label is the moment to add the option, not before. */
export function field(
  id: string,
  label: string,
  attrs: Record<string, unknown> = {},
): Field {
  const input = (
    <input class="otp-input" id={id} required {...attrs} />
  ) as HTMLInputElement;
  return {
    nodes: [
      <label class="otp-label otp-visually-hidden" for={id}>{label}</label>,
      input,
    ],
    input,
  };
}

export interface GatedButton {
  el: HTMLButtonElement;
  /** Re-apply the gate. Needed after clearBusy, which re-enables every control
   * in its container — this button included. */
  refresh(): void;
}

/** A primary submit button enabled only while `ready()` holds.
 *
 * Three screens each had their own copy of "start disabled, listen for input,
 * re-evaluate": the entry step's email address, the code step's six digits, the
 * registration form's two names. The disabled look comes from the existing
 * `.otp-btn:disabled` rule. */
export function gatedButton(opts: {
  label: string;
  ready(): boolean;
  /** Inputs whose typing can change the answer. A value that doesn't come from
   * an `<input>` the button can watch — the code cells, which report through
   * their own component — calls `refresh()` instead. */
  watch?: HTMLInputElement[];
}): GatedButton {
  const el = (
    <button class="otp-btn otp-btn-primary" type="submit" disabled>
      {opts.label}
    </button>
  ) as HTMLButtonElement;

  function refresh(): void {
    el.disabled = !opts.ready();
  }

  for (const input of opts.watch ?? []) {
    input.addEventListener("input", refresh);
  }
  refresh();
  return { el, refresh };
}

/** Disable every control in `container` and put the submitting form's button
 * in its loading state. Pass the same container to clearBusy to undo. */
export function startBusy(container: HTMLElement, form: HTMLFormElement): void {
  setControlsDisabled(container, true);
  form.querySelector('button[type="submit"]')?.classList.add("is-loading");
}

export function clearBusy(container: HTMLElement): void {
  setControlsDisabled(container, false);
  container.querySelectorAll(".otp-btn.is-loading").forEach((node) => {
    node.classList.remove("is-loading");
  });
}

function setControlsDisabled(container: HTMLElement, disabled: boolean): void {
  container
    .querySelectorAll<HTMLInputElement | HTMLButtonElement>("input, button")
    .forEach((node) => {
      node.disabled = disabled;
    });
  container.querySelectorAll("a.otp-btn").forEach((node) => {
    node.classList.toggle("is-disabled", disabled);
  });
}
